import { useMemo, useState } from 'react';

import { Modal, dialogService } from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { useShallow } from 'zustand/react/shallow';

import { ModalContentsContainer } from '~/components/common';

import { usePostVendorRelease, useStockReleaseRegisterValidation } from '~/hooks/stock';
import { useStockReleaseStore } from '~/store';

import { StockReleaseRegisterFirstStepContent } from './StockReleaseRegisterFirstStepContent';
import { StockReleaseRegisterSecondStepContent } from './StockReleaseRegisterSecondStepContent';

import { TypeStockReleasePallet, TypeStockReleaseRegisterDetail } from '~/types/stock.types';

type TypeStockReleaseRegisterModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * [재고관리 > 반출요청관리] 반출요청등록 모달
 */
const StockReleaseRegisterModal = ({ open, onCancel, onConfirm }: TypeStockReleaseRegisterModalProps) => {
  const {
    cluster,
    requestGoods,
    detailInfo,
    palletInfo,
    agreeInfo,
    recentInfo,
    resetStockRelease,
    resetStockReleaseInfo,
  } = useStockReleaseStore(
    useShallow((state) => ({
      cluster: state.cluster,
      requestGoods: state.requestGoods,
      detailInfo: state.detailInfo,
      palletInfo: state.palletInfo,
      agreeInfo: state.agreeInfo,
      recentInfo: state.recentInfo,
      resetStockRelease: state.actions.resetStockRelease,
      resetStockReleaseInfo: state.actions.resetStockReleaseInfo,
    }))
  );

  const { requestGoodsRef, errorFieldRefs, validateRequestGoods, validationRegisterData } =
    useStockReleaseRegisterValidation();
  const { requestVendorReleaseRegister } = usePostVendorRelease();

  const [currentStep, setCurrentStep] = useState(1);

  const isUpdatedInfo = useMemo(
    () =>
      !isDeepEqual(detailInfo, recentInfo?.destinationAddress ?? null) ||
      !isDeepEqual(palletInfo, recentInfo?.palletInfo ?? null),
    [detailInfo, palletInfo, recentInfo]
  );

  // 모달 스텝 변경 처리
  const handleStepChange = (step: number) => {
    if (step === 1) {
      if (isUpdatedInfo || agreeInfo) {
        dialogService.addDialog({
          type: 'confirm',
          cancelText: '취소',
          confirmText: '이전으로',
          children: `작성된 내용이 저장되지 않습니다.\n이전 페이지로 이동하시겠습니까?`,
          onConfirm: () => {
            setCurrentStep(1);
            resetStockReleaseInfo();
          },
        });
      } else {
        setCurrentStep(1);
      }
    }

    if (step === 2) {
      const { isValid, message } = validateRequestGoods();
      if (!isValid) {
        dialogService.addDialog({
          children: message,
        });
        return;
      }
      setCurrentStep(2);
    }
  };

  // 모달 취소 버튼 클릭 시 처리
  const handleCancel = () => {
    if (requestGoods.length > 0 || isUpdatedInfo || agreeInfo) {
      dialogService.addDialog({
        type: 'confirm',
        cancelText: '취소',
        confirmText: '확인',
        children: `작성된 내용이 저장되지 않습니다.\n창을 닫으시겠습니까?`,
        onConfirm: () => {
          onCancel();
          resetStockRelease();
        },
      });
    } else {
      onCancel();
    }
  };

  // 모달 확인 버튼 클릭 시 처리
  const handleConfirm = () => {
    const { isValid } = validationRegisterData();

    if (isValid) {
      dialogService.addDialog({
        type: 'confirm',
        cancelText: '취소',
        confirmText: '등록',
        children: '요청을 등록하시겠습니까?',
        onConfirm: () => {
          requestVendorReleaseRegister(
            {
              clusterCode: cluster?.value ?? '',
              detailInfo: detailInfo as TypeStockReleaseRegisterDetail,
              palletInfo: palletInfo as TypeStockReleasePallet,
              requestSkus: requestGoods,
            },
            {
              onSuccess: () => {
                resetStockRelease();
                onConfirm();
              },
            }
          );
        },
      });
    }
  };

  return (
    <Modal
      title="반출 요청 등록"
      size="xlarge"
      confirmText="등록"
      open={open}
      step={{
        steps: [{ title: '반출 상품 입력' }, { title: '반출 예정 정보 입력' }],
        currentStep,
        onStepChange: handleStepChange,
      }}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    >
      <ModalContentsContainer>
        {currentStep === 1 && <StockReleaseRegisterFirstStepContent requestGoodsRef={requestGoodsRef} />}
        {currentStep === 2 && <StockReleaseRegisterSecondStepContent errorFieldRefs={errorFieldRefs} />}
      </ModalContentsContainer>
    </Modal>
  );
};

export default StockReleaseRegisterModal;
